import { BusinessObjectBase } from './base';
import { groupMatchEntrySchema } from '@schemas/business-objects/group-match-entry';
import type { GroupMatch } from './group-match';
import type { Match } from './match';
import type { Id } from '../base-types';

export interface GroupMatchEntryData {
  readonly id: Id;
  readonly groupMatchId: Id;
  readonly groupMatch?: GroupMatch;
  readonly matchId: Id;
  readonly match?: Match;
}

export interface GroupMatchEntry extends Readonly<GroupMatchEntryData> {}
type GroupMatchEntryCreateWithChildrenData = Partial<GroupMatchEntryData>;

export class GroupMatchEntry extends BusinessObjectBase {
  static readonly entityName = 'GroupMatchEntry' as const;
  static readonly plural = 'group-match-entries';
  static schema = groupMatchEntrySchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatchEntry[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new GroupMatchEntry(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatchEntry | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new GroupMatchEntry(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<GroupMatchEntry> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new GroupMatchEntry(data);
  }

  static async create(
    data: Omit<GroupMatchEntryData, 'id'>,
  ): Promise<GroupMatchEntry> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new GroupMatchEntry(result);
  }

  static async createMany(
    data: Omit<GroupMatchEntryData, 'id'>[],
  ): Promise<GroupMatchEntry[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new GroupMatchEntry(item));
  }

  static async createWithChildren(
    data: GroupMatchEntryCreateWithChildrenData,
  ): Promise<GroupMatchEntry> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new GroupMatchEntry(result);
  }

  static async createManyWithChildren(
    data: GroupMatchEntryCreateWithChildrenData[],
  ): Promise<GroupMatchEntry[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new GroupMatchEntry(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<GroupMatchEntryData>,
  ): Promise<GroupMatchEntry> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new GroupMatchEntry(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
